if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      
        this.form = this.querySelector('form');
        
        // Guard clause if form doesn't exist
        if (!this.form) {
          console.error('Product form not found');
          return;
        }
      
        this.maxQuantityPerSku = 10;
        this.currentCartQuantity = 0;
        
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        
        // Find variant input and preserve its initial disabled state
        this.variantInput = this.form.querySelector('[name="id"]');
        if (this.variantInput) {
          // Store initial state instead of forcing to false
          this.variantInputInitiallyDisabled = this.variantInput.disabled;
        } else {
          console.error('Variant input not found in form');
        }
        
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        
        // Look for submit button and preserve its initial disabled state
        this.submitButton = this.form.querySelector('[type="submit"]');
        if (!this.submitButton) {
          console.error('Submit button not found in product form');
          return;
        }
        
        // Store the initial disabled state
        this.submitButtonInitiallyDisabled = this.submitButton.disabled;
        
        this.submitButtonText = this.submitButton.querySelector('span') || this.submitButton;
      
        // Only proceed with quantity management if product is not sold out
        if (!this.submitButtonInitiallyDisabled) {
          // Continue with the rest of initialization
          if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      
          this.hideErrors = this.dataset.hideErrors === 'true';
          
          this.ensureLoadingSpinnerExists();
          this.findQuantityElements();
          this.createMaxQuantityMessage();
          this.initializeCartQuantity();
        }
      }
      
      // Initialize cart quantity and block interaction until loaded
      async initializeCartQuantity() {
        // If product was initially sold out, don't modify disabled states
        if (this.submitButtonInitiallyDisabled) {
          return;
        }
        
        try {
          // Temporarily disable form
          if (this.submitButton) {
            this.submitButton.disabled = true;
          }
          
          if (this.quantityInput) {
            this.quantityInput.disabled = true;
          }
          
          if (this.quantityPlus) {
            this.quantityPlus.disabled = true;
          }
          
          if (this.quantityMinus) {
            this.quantityMinus.disabled = true;
          }
          
          // Fetch cart data
          await this.fetchCurrentCartQuantity();
          
          // Enable form and update UI
          this.checkCurrentCartQuantity(true);
          
          // Set up event listeners after we have cart data
          this.setupQuantityListeners();
        } catch (error) {
          console.error('Error initializing cart quantity:', error);
          
          // Re-enable form in case of error
          if (this.submitButton) {
            this.submitButton.disabled = false;
          }
          
          if (this.quantityInput) {
            this.quantityInput.disabled = false;
          }
          
          if (this.quantityPlus) {
            this.quantityPlus.disabled = false;
          }
          
          if (this.quantityMinus) {
            this.quantityMinus.disabled = false;
          }
        }
      }
      
      // Set up listeners to enforce max quantity in real-time
      setupQuantityListeners() {
        if (!this.quantityInput) return;
        
        // Listen for quantity changes
        if (this.quantityInput) {
          this.quantityInput.addEventListener('change', () => {
            this.validateQuantityInput();
          });
          
          this.quantityInput.addEventListener('input', () => {
            this.validateQuantityInput();
          });
          
          // Also validate on focus to catch any external changes
          this.quantityInput.addEventListener('focus', () => {
            this.validateQuantityInput();
          });
        }
        
        // Listen for plus button clicks
        if (this.quantityPlus) {
          const originalClickHandler = this.quantityPlus.onclick;
          
          this.quantityPlus.onclick = (event) => {
            // Always validate first
            if (!this.validatePlusClick()) {
              event.preventDefault();
              event.stopPropagation();
              return false;
            }
            
            // If valid, let the original handler run
            if (originalClickHandler) {
              return originalClickHandler.call(this.quantityPlus, event);
            }
          };
        }
        
        // Finally, apply the validation right now
        this.validateQuantityInput();
      }
      
      // Validate plus button clicks
      validatePlusClick() {
        if (!this.quantityInput) return true;
        
        const currentValue = parseInt(this.quantityInput.value || 1);
        const remainingAllowed = this.maxQuantityPerSku - this.currentCartQuantity;
        
        // If already at or exceeding max allowed
        if (currentValue >= remainingAllowed) {
          // Show a temporary message
          this.showTemporaryMessage(`Cannot add more. Maximum is ${this.maxQuantityPerSku} and you have ${this.currentCartQuantity} in cart`);
          
          // Disable the button visually
          this.disablePlusButton();
          
          return false;
        }
        
        return true;
      }
      
      // Disable the plus button
      disablePlusButton() {
        if (!this.quantityPlus) return;
        
        this.quantityPlus.disabled = true;
        this.quantityPlus.setAttribute('aria-disabled', 'true');
        this.quantityPlus.style.opacity = '0.5';
        this.quantityPlus.style.cursor = 'not-allowed';
        
        // Add an explanatory tooltip
        this.quantityPlus.title = `Maximum quantity (${this.maxQuantityPerSku}) would be exceeded. You already have ${this.currentCartQuantity} in cart.`;
      }
      
      // Enable the plus button
      enablePlusButton() {
        if (!this.quantityPlus) return;
        
        this.quantityPlus.disabled = false;
        this.quantityPlus.removeAttribute('aria-disabled');
        this.quantityPlus.style.opacity = '1';
        this.quantityPlus.style.cursor = 'pointer';
        this.quantityPlus.title = '';
      }
      
      // Fetch current cart quantity for the selected variant
      async fetchCurrentCartQuantity() {
        if (!this.variantInput) return 0;
        
        try {
          const response = await fetch(`${routes.cart_url}.js`);
          if (!response.ok) throw new Error('Failed to fetch cart');
          
          const cart = await response.json();
          
          // Find if this variant exists in cart
          const variantId = parseInt(this.variantInput.value);
          const existingItem = cart.items.find(item => item.variant_id === variantId);
          
          // Update the stored quantity
          this.currentCartQuantity = existingItem ? existingItem.quantity : 0;
          
          return this.currentCartQuantity;
        } catch (error) {
          console.error('Error fetching cart quantity:', error);
          return 0;
        }
      }
      
      // Validate quantity input to prevent exceeding max
      validateQuantityInput() {
        if (!this.quantityInput) return;
        
        const newQuantity = parseInt(this.quantityInput.value || 1);
        const remainingAllowed = this.maxQuantityPerSku - this.currentCartQuantity;
        
        // If trying to add more than allowed
        if (newQuantity > remainingAllowed) {
          // Set to max allowed - minimum 1
          const adjustedQuantity = Math.max(remainingAllowed, 1);
          this.quantityInput.value = adjustedQuantity;
          
          // Show a temporary message
          // this.showTemporaryMessage(`You can only add ${remainingAllowed} more (${this.currentCartQuantity} already in cart)`);
          
          // Disable plus button
          this.disablePlusButton();
        } else if (newQuantity < remainingAllowed) {
          // Enable plus button if we're below the max
          this.enablePlusButton();
        } else {
          // We're exactly at the max, disable plus button
          this.disablePlusButton();
        }
        
        // If completely at max, consider disabling the form
        if (remainingAllowed <= 0) {
          this.disableForm(true);
        }
        
        // Also update max attribute on input
        this.quantityInput.setAttribute('max', remainingAllowed);
      }
      
      // Show a temporary message
      showTemporaryMessage(message) {
        // Create or get the message element
        let messageElement = document.querySelector('.quantity-limit-message');
        if (!messageElement) {
          messageElement = document.createElement('div');
          messageElement.classList.add('quantity-limit-message');
          messageElement.style.color = '#e74c3c';
          messageElement.style.fontWeight = 'bold';
          messageElement.style.marginTop = '5px';
          
          // Insert near quantity input if possible
          const container = this.form.querySelector('.product-form__quantity') ||
                          this.form.querySelector('.quantity') ||
                          (this.quantityInput && this.quantityInput.parentNode);
          
          if (container) {
            container.appendChild(messageElement);
          } else if (this.submitButton && this.submitButton.parentNode) {
            this.submitButton.parentNode.insertBefore(messageElement, this.submitButton);
          }
        }
        
        // Set the message and show it
        messageElement.textContent = message;
        messageElement.style.display = 'block';
        
        // Hide after 3 seconds
        setTimeout(() => {
          messageElement.style.display = 'none';
        }, 3000);
      }
      
      // Find quantity elements based on the specific theme structure
      findQuantityElements() {
        // First look for the quantity-input component
        const quantityComponent = document.querySelector('quantity-input');
        
        if (quantityComponent) {
          // Find the inner input within the component
          this.quantityInput = quantityComponent.querySelector('.product-form__input .quantity__input');
          this.quantityMinus = quantityComponent.querySelector('.product-form__input .quantity__button[name="minus"]');
          this.quantityPlus = quantityComponent.querySelector('.product-form__input .quantity__button[name="plus"]');
          
          // Find the quantity label
          this.quantityLabel = document.querySelector('.quantity__label');
        } else {
          // Fallback to more generic selectors
          this.quantityInput = document.querySelector('[name="quantity"]');
          this.quantityMinus = document.querySelector('.quantity__button[name="minus"]') || 
                               document.querySelector('button[name="minus"]');
          this.quantityPlus = document.querySelector('.quantity__button[name="plus"]') || 
                             document.querySelector('button[name="plus"]');
          this.quantityLabel = document.querySelector('.quantity__label') || 
                              document.querySelector('label[for*="Quantity"]');
        }
        
        if (!this.quantityInput) {
          console.warn('Quantity input not found, creating a fallback');
          // Create a hidden fallback input
          this.quantityInput = document.createElement('input');
          this.quantityInput.type = 'hidden';
          this.quantityInput.name = 'quantity';
          this.quantityInput.value = '1';
          this.form.appendChild(this.quantityInput);
        }
      }
      
      // Create max quantity message
      createMaxQuantityMessage() {
        this.maxQuantityMessage = document.createElement('div');
        this.maxQuantityMessage.classList.add('max-quantity-message');
        this.maxQuantityMessage.textContent = 'You have reached the maximum quantity allowed (10)';
        this.maxQuantityMessage.style.color = '#e74c3c';
        this.maxQuantityMessage.style.marginTop = '10px';
        this.maxQuantityMessage.style.display = 'none';
        
        // Add the message after the submit button
        if (this.submitButton && this.submitButton.parentNode) {
          this.submitButton.parentNode.insertBefore(this.maxQuantityMessage, this.submitButton.nextSibling);
        }
      }
      
      // Method to check current cart quantity and update UI
      async checkCurrentCartQuantity(skipFetch = false) {
        if (!this.variantInput) {
          console.error('Missing variant input for cart quantity check');
          return;
        }
        
        try {
          // Fetch current cart if needed
          if (!skipFetch) {
            await this.fetchCurrentCartQuantity();
          }
                    
          // Update quantity input label to show current cart quantity
          if (this.quantityLabel) {
            // Keep original text if it exists
            const originalText = this.quantityLabel.textContent.split('(')[0].trim() || 'QUANTITY';
            this.quantityLabel.innerHTML = `${originalText} <span class="current-in-cart">(${this.currentCartQuantity} in cart)</span>`;
          }
          
          // If already at max quantity
          if (this.currentCartQuantity >= this.maxQuantityPerSku) {
            this.disableForm(true);
            return;
          }
          
          // Set the max attribute on the quantity input
          const remainingQuantity = this.maxQuantityPerSku - this.currentCartQuantity;
          if (this.quantityInput) {
            this.quantityInput.setAttribute('max', remainingQuantity);
            
            // If current value is more than remaining, adjust it
            if (parseInt(this.quantityInput.value || 1) > remainingQuantity) {
              this.quantityInput.value = remainingQuantity;
            }
            
            // Update max quantity display in quantity rules if exists
            const maxQtyRule = document.querySelector('.quantity__rules .divider:last-child');
            if (maxQtyRule) {
              maxQtyRule.textContent = `Maximum of ${remainingQuantity}`;
            }
            
            // Enable form elements
            this.disableForm(false);
            
            // Validate the quantity input
            this.validateQuantityInput();
          }
        } catch (error) {
          console.error('Error checking cart quantities:', error);
        }
      }
      
      // Method to disable/enable form
      disableForm(disable) {
        if (disable) {
          // Disable quantity input and buttons
          if (this.quantityInput) {
            this.quantityInput.disabled = true;
            this.quantityInput.style.opacity = '0.5';
          }
          
          if (this.quantityMinus) {
            this.quantityMinus.disabled = true;
            this.quantityMinus.style.opacity = '0.5';
          }
          
          if (this.quantityPlus) {
            this.quantityPlus.disabled = true;
            this.quantityPlus.style.opacity = '0.5';
          }
          
          // Disable submit button
          if (this.submitButton) {
            this.submitButton.disabled = true;
            this.submitButton.classList.add('disabled');
            this.submitButton.style.opacity = '0.5';
            this.submitButton.setAttribute('aria-disabled', 'true');
          }
          
          // Show max quantity message
          if (this.maxQuantityMessage) {
            this.maxQuantityMessage.style.display = 'block';
          }
        } else {
          // Enable quantity input and buttons
          if (this.quantityInput) {
            this.quantityInput.disabled = false;
            this.quantityInput.style.opacity = '1';
          }
          
          if (this.quantityMinus) {
            this.quantityMinus.disabled = false;
            this.quantityMinus.style.opacity = '1';
          }
          
          // Only enable plus if we're not at max
          const newQuantity = parseInt(this.quantityInput.value || 1);
          const remainingAllowed = this.maxQuantityPerSku - this.currentCartQuantity;
          
          if (newQuantity < remainingAllowed) {
            this.enablePlusButton();
          } else {
            this.disablePlusButton();
          }
          
          // Enable submit button only if we can add more
          if (this.submitButton) {
            if (remainingAllowed > 0) {
              this.submitButton.disabled = false;
              this.submitButton.classList.remove('disabled');
              this.submitButton.style.opacity = '1';
              this.submitButton.removeAttribute('aria-disabled');
            } else {
              this.submitButton.disabled = true;
              this.submitButton.classList.add('disabled');
              this.submitButton.style.opacity = '0.5';
              this.submitButton.setAttribute('aria-disabled', 'true');
            }
          }
          
          // Hide max quantity message if we can add more
          if (this.maxQuantityMessage) {
            this.maxQuantityMessage.style.display = remainingAllowed > 0 ? 'none' : 'block';
          }
        }
      }

      // Add this method to check current cart quantities
      async checkCartQuantities(variantId, quantityToAdd) {
        try {
          // Fetch current cart
          const response = await fetch(`${routes.cart_url}.js`);
          if (!response.ok) throw new Error('Failed to fetch cart');
          
          const cart = await response.json();
          
          // Find if this variant already exists in cart
          const existingItem = cart.items.find(item => item.variant_id === variantId);
          const currentQuantity = existingItem ? existingItem.quantity : 0;
          
          // Update stored current quantity
          this.currentCartQuantity = currentQuantity;

          
          // Check if adding this quantity would exceed our limit
          if ((currentQuantity + parseInt(quantityToAdd)) > this.maxQuantityPerSku) {
            const remainingQuantity = this.maxQuantityPerSku - currentQuantity;
            return {
              valid: false,
              message: remainingQuantity > 0 
                ? `You can only add ${remainingQuantity} more of this item. You already have ${currentQuantity} in your cart.`
                : `You have reached the maximum quantity (${this.maxQuantityPerSku}) for this item.`
            };
          }
          
          return { valid: true };
        } catch (error) {
          console.error('Error checking cart quantities:', error);
          return { valid: true }; // Fail open in case of errors
        }
      }
      
      // Helper method to ensure the loading spinner exists
      ensureLoadingSpinnerExists() {
        if (!this.submitButton) return;
        
        let loadingSpinner = this.submitButton.querySelector('.loading__spinner');
        if (!loadingSpinner) {
          loadingSpinner = document.createElement('div');
          loadingSpinner.classList.add('loading__spinner', 'hidden');
          loadingSpinner.innerHTML = `
            <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          `;
          this.submitButton.appendChild(loadingSpinner);
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        
        // Force a fresh cart check before proceeding
        await this.fetchCurrentCartQuantity();
        
        // Guard clause for missing elements
        if (!this.submitButton || !this.form) {
          console.error('Missing submit button or form');
          return;
        }
        
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        this.handleErrorMessage();

        // Guard against missing variant input
        if (!this.variantInput) {
          console.error('Cannot proceed: Missing variant ID input');
          this.handleErrorMessage('There was a problem with the form. Please refresh and try again.');
          return;
        }
        
        // If quantity input is missing for some reason, reinitialize it
        if (!this.quantityInput) {
          this.findQuantityElements();
          if (!this.quantityInput) {
            // Create a fallback
            this.quantityInput = document.createElement('input');
            this.quantityInput.type = 'hidden';
            this.quantityInput.name = 'quantity';
            this.quantityInput.value = '1';
            this.form.appendChild(this.quantityInput);
          }
        }
        
        const variantId = parseInt(this.variantInput.value);
        const quantity = parseInt(this.quantityInput.value || 1);
        
        // Critical check - ensure we're not exceeding the limit
        if (quantity + this.currentCartQuantity > this.maxQuantityPerSku) {
          const remainingAllowed = this.maxQuantityPerSku - this.currentCartQuantity;
          
          if (remainingAllowed <= 0) {
            this.handleErrorMessage(`You already have the maximum quantity (${this.maxQuantityPerSku}) in your cart.`);
            return;
          }
          
          if (quantity > remainingAllowed) {
            // Adjust quantity and show message
            this.quantityInput.value = remainingAllowed;
            this.handleErrorMessage(`Cannot add ${quantity} more. Adjusted to ${remainingAllowed} to avoid exceeding maximum (${this.maxQuantityPerSku}).`);
            
            // Give user a chance to see the message
            return;
          }
        }
        
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        
        // Add this check before proceeding with add to cart
        const quantityCheck = await this.checkCartQuantities(variantId, quantity);
        if (!quantityCheck.valid) {
          // Show error message
          this.handleErrorMessage(quantityCheck.message);
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          return;
        }
        
        // Safely access the loading spinner
        const loadingSpinner = this.submitButton.querySelector('.loading__spinner');
        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden');
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (typeof publish === 'function') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error && typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }
            
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                    // Check cart quantity after adding
                    this.checkCurrentCartQuantity();
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else if (this.cart) {
              this.cart.renderContents(response);
              // Check cart quantity after adding
              this.checkCurrentCartQuantity();
            }
          })
          .catch((e) => {
            console.error('Error adding to cart:', e);
          })
          .finally(() => {
            if (!this.submitButton) return;
            
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
            
            if (!this.error) {
              this.submitButton.removeAttribute('aria-disabled');
            }
            
            const loadingSpinner = this.submitButton.querySelector('.loading__spinner');
            if (loadingSpinner) {
              loadingSpinner.classList.add('hidden');
            }
          });
      }

      // Make sure you have an error handling method
      handleErrorMessage(message = '') {
        // console.log('Showing error message:', message);
      }

      createErrorElement() {
        const errorElement = document.createElement('div');
        errorElement.classList.add('product-form__error-message');
        errorElement.setAttribute('role', 'alert');
        errorElement.style.color = '#e74c3c';
        errorElement.style.marginTop = '10px';
        
        const container = this.querySelector('.product-form__buttons') || 
                          this.form.querySelector('.product-form__buttons') ||
                          this.form;
                          
        container.prepend(errorElement);
        
        return errorElement;
      }
    }
  );
}